import { useCallback, useEffect, useRef } from "react";

import { Box, Checkbox, Flex, SimpleGrid } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { IconCirclePlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, DECIMAL_PROPS, NumberInput, Text } from "components";

import {
  BankCreditRequestForm,
  BankCreditRequestMortgageResponse,
  BankCreditResponse,
} from "types";
import { AppUtils } from "utils";

interface BankCreditRequestMortgageProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  readOnly?: boolean;
}

type MortgageFormItem = Omit<BankCreditRequestMortgageResponse, "id" | "creditRequestId">;

type MortgageFormData = Omit<BankCreditRequestMortgageResponse, "id"> & { id?: number };

const tempMortgageObject: Partial<MortgageFormItem> = {};

const propertyName = "mortgageExpenses";

export const BankCreditRequestMortgage = ({
  credit,
  form,
  readOnly = false,
}: BankCreditRequestMortgageProps) => {
  const { t } = useTranslation();
  const didInitialSync = useRef(false);
  const includeAmortization = form.getValues().includeAmortization === true;
  const amortizationAmount = form.getValues().amortizationAmount;
  const amortizationType = form.getValues().amortizationType;

  const getInitialData = useCallback(() => {
    const formValues: Partial<BankCreditRequestMortgageResponse>[] | undefined =
      credit[propertyName];
    let array = [];
    if (!formValues || formValues.length === 0) {
      array.push(tempMortgageObject);
    } else {
      array = formValues.map((el) => ({ ...el }));
    }
    return array;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateMonthlyMortgage = useCallback(
    (
      mortgageAmount: number,
      interestRate: number,
      bank: "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNERCARD_KREDIT",
      amortizationShare = 0,
    ) => {
      if (mortgageAmount <= 0) return undefined;

      // always use 3% interest rate
      if (bank === "BANK_NOW" || bank === "CORNERCARD_KREDIT") {
        return (mortgageAmount * 3) / 100 / 12;
      }

      // Cembra uses the actual interest rate from input + 1%
      if (bank === "CEMBRA_MONEY_AG" && interestRate > 0) {
        return (mortgageAmount * (interestRate + 1)) / 100 / 12 + amortizationShare;
      }

      return undefined;
    },
    [],
  );

  const calculateTotalMonthlyMortgage = useCallback(
    (mortgageAmount: number, interestRate: number) => {
      if (mortgageAmount <= 0) return undefined;

      return (mortgageAmount * (interestRate / 100)) / 12;
    },
    [],
  );

  const calculateMonthlyAmortization = useCallback(() => {
    if (!includeAmortization || amortizationAmount == null || amortizationAmount <= 0) {
      return 0;
    }

    if (amortizationType === "MONTHLY") {
      return amortizationAmount;
    }

    if (amortizationType === "QUARTERLY") {
      return amortizationAmount / 3;
    }

    if (amortizationType === "ANNUAL") {
      return amortizationAmount / 12;
    }

    return 0;
  }, [amortizationAmount, amortizationType, includeAmortization]);

  const calculateMonthlyAmortizationShare = useCallback(
    (values: any, mortgageAmount: number) => {
      if (mortgageAmount <= 0) return 0;

      const totalMortgageAmount = Object.keys(values).reduce((sum, key) => {
        const rowMortgageAmount = values[key]?.mortgageAmount;

        if (rowMortgageAmount == null || rowMortgageAmount <= 0) {
          return sum;
        }

        return sum + rowMortgageAmount;
      }, 0);

      const monthlyAmortization = calculateMonthlyAmortization();

      if (totalMortgageAmount <= 0 || monthlyAmortization <= 0) {
        return 0;
      }

      return (monthlyAmortization * mortgageAmount) / totalMortgageAmount;
    },
    [calculateMonthlyAmortization],
  );

  const syncMortgageValues = useCallback(
    (values: any) => {
      const tempArray: MortgageFormData[] = [];
      let totalMonthlyMortgage = 0;

      Object.keys(values).forEach((key) => {
        const item: Partial<BankCreditRequestMortgageResponse> = { ...values[key] };

        const isNotEmpty = Object.keys(item).some(
          (field) =>
            field !== "id" &&
            field !== "creditRequestId" &&
            item[field as keyof BankCreditRequestMortgageResponse] != null,
        );

        if (isNotEmpty) {
          const mortgageAmount = item.mortgageAmount || 0;
          const interestRate = item.interestRate || 0;
          const monthlyAmortizationShare = calculateMonthlyAmortizationShare(values, mortgageAmount);

          const monthlyBn = calculateMonthlyMortgage(mortgageAmount, interestRate, "BANK_NOW");
          const monthlyCb = calculateMonthlyMortgage(
            mortgageAmount,
            interestRate,
            "CEMBRA_MONEY_AG",
            monthlyAmortizationShare,
          );
          const monthlyCc = calculateMonthlyMortgage(
            mortgageAmount,
            interestRate,
            "CORNERCARD_KREDIT",
          );

          const backendItem: MortgageFormData = {
            ...item,
            creditRequestId: credit.id,
            monthlyMortgageBn: monthlyBn,
            monthlyMortgageCb: monthlyCb,
            monthlyMortgageCc: monthlyCc,
          };
          tempArray.push(backendItem);

          const shouldIncludeInTotal =
            item.mortgageAndInterestRate === true &&
            item.mortgageAmount != null &&
            item.interestRate != null;

          if (shouldIncludeInTotal) {
            const monthlyTotal = calculateTotalMonthlyMortgage(mortgageAmount, interestRate);

            totalMonthlyMortgage += monthlyTotal || 0;
          }
        }
      });

      form.setFieldValue(propertyName, tempArray as any);
      form.setFieldValue(
        "mortgageAmount",
        totalMonthlyMortgage > 0 ? totalMonthlyMortgage : undefined,
      );
    },
    [
      calculateMonthlyAmortizationShare,
      calculateMonthlyMortgage,
      calculateTotalMonthlyMortgage,
      credit.id,
      form,
    ],
  );

  const mortgageForm = useForm({
    mode: "uncontrolled",
    initialValues: getInitialData(),
    onValuesChange(values) {
      updateCredit(values);
    },
  });

  const updateCredit = useDebouncedCallback(syncMortgageValues, 300);

  const currentMortgageValues = mortgageForm.getValues();

  useEffect(() => {
    if (!didInitialSync.current) {
      didInitialSync.current = true;
      syncMortgageValues(mortgageForm.getValues());
      return;
    }

    syncMortgageValues(mortgageForm.getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAmortization, amortizationAmount, amortizationType]);

  const getMonthlyMortgageForDisplay = (
    index: number,
    bank: "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNERCARD_KREDIT",
  ) => {
    const mortgage = currentMortgageValues[index];
    if (!mortgage) return undefined;

    const mortgageAmount = mortgage.mortgageAmount || 0;
    const interestRate = mortgage.interestRate || 0;
    const monthlyAmortizationShare = calculateMonthlyAmortizationShare(
      currentMortgageValues,
      mortgageAmount,
    );

    return calculateMonthlyMortgage(mortgageAmount, interestRate, bank, monthlyAmortizationShare);
  };

  const formatMonthlyMortgage = (value?: number) =>
    value == null
      ? "0 CHF"
      : `${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} CHF`;

  const mortgageKeys = Object.keys(currentMortgageValues);

  return (
    <Box mt={"md"}>
      {mortgageKeys.map((key: any, index: number) => {
        const monthlyMortgageBn = getMonthlyMortgageForDisplay(index, "BANK_NOW");
        const monthlyMortgageCb = getMonthlyMortgageForDisplay(index, "CEMBRA_MONEY_AG");
        const monthlyMortgageCc = getMonthlyMortgageForDisplay(index, "CORNERCARD_KREDIT");

        return (
          <Box key={key} mb={"sm"}>
            <SimpleGrid cols={4} mb="md">
              <Box>
                <Checkbox
                  key={mortgageForm.key(`${index}.mortgageAndInterestRate`)}
                  {...mortgageForm.getInputProps(`${index}.mortgageAndInterestRate`, {
                    type: "checkbox",
                  })}
                  disabled={readOnly}
                  label={t("credit.mortgageAmountAndRate.label")}
                  mb={4}
                />
                <Flex gap="sm">
                  <Box style={{ flex: "8" }}>
                    <NumberInput
                      placeholder={t("credit.mortgageAmount.placeholder")}
                      key={mortgageForm.key(`${index}.mortgageAmount`)}
                      {...mortgageForm.getInputProps(`${index}.mortgageAmount`)}
                      min={0}
                      disabled={readOnly}
                    />
                  </Box>
                  <Box style={{ flex: "4" }}>
                    <NumberInput
                      placeholder={t("credit.interestRate.placeholder")}
                      key={mortgageForm.key(`${index}.interestRate`)}
                      {...mortgageForm.getInputProps(`${index}.interestRate`)}
                      min={0}
                      max={100}
                      {...DECIMAL_PROPS}
                      suffix=" %"
                      disabled={readOnly}
                    />
                  </Box>
                </Flex>

                <Box mt={0}>
                  <Flex justify="space-between" align="center" gap="md">
                    <Text size="sm" c="dimmed">
                      {t("credit.monthlyCostsBnCc.label")}
                    </Text>
                    <Text size="sm" fw={500} c="dimmed">
                      {formatMonthlyMortgage(monthlyMortgageBn)}
                    </Text>
                  </Flex>

                  <Flex justify="space-between" align="center" gap="md">
                    <Text size="sm" c="dimmed">
                      {t("credit.monthlyCostsCb.label")}
                    </Text>
                    <Text size="sm" fw={500} c="dimmed">
                      {formatMonthlyMortgage(monthlyMortgageCb)}
                    </Text>
                  </Flex>
                </Box>
              </Box>

              <NumberInput
                label={t("credit.monthlyMortgageBn.label")}
                value={monthlyMortgageBn}
                min={0}
                {...DECIMAL_PROPS}
                disabled
                readOnly
              />

              <NumberInput
                label={t("credit.monthlyMortgageCb.label")}
                value={monthlyMortgageCb}
                min={0}
                {...DECIMAL_PROPS}
                disabled
                readOnly
              />

              <NumberInput
                label={t("credit.monthlyMortgageCc.label")}
                value={monthlyMortgageCc}
                min={0}
                {...DECIMAL_PROPS}
                disabled
                readOnly
              />
            </SimpleGrid>
          </Box>
        );
      })}

      {!readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(mortgageForm, tempMortgageObject)}
          variant="transparent"
          leftSection={<IconCirclePlus size={18} />}
        >
          {t("credit.addMortgage.label")}
        </Button>
      )}
    </Box>
  );
};
